import { useState, useEffect } from 'react'
import axios from 'axios'

const SalesReport = () => {
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [productMasterCode, setProductMasterCode] = useState('')
  const [productColor, setProductColor] = useState('')
  const [products, setProducts] = useState([])
  const [colors, setColors] = useState([])
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchProducts()
    fetchColors()
  }, [])

  const fetchProducts = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/get-products`,
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )
      if (response.data && response.data.data) {
        setProducts(response.data.data)
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch products")
    } finally {
      setIsLoading(false)
    }
  }

  const fetchColors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/get-product-colors`,
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )
      if (response.data && response.data.data) {
        setColors(response.data.data)
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch colors")
    }
  }

  const handleExport = async () => {
    try {
      setIsExporting(true)
      setError(null)

      if (!productMasterCode || !productColor) {
        setError("Please select both Product and Color")
        setIsExporting(false)
        return
      }

      const safeColor = productColor.replace(/\s+/g, ''); // remove all spaces
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/export-sales-report/${productMasterCode}/${safeColor}`,
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      )

      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = `sales-report-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err.response?.data?.message || "Failed to export sales report")
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-2">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-1">Sales Report</h1>
          <p className="text-gray-500">Generate and export sales reports</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Product Details</h3>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">
                  Product Code
                </label>
                <select
                  value={productMasterCode}
                  onChange={(e) => setProductMasterCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                >
                  <option value="">Select Product</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.product_code}>
                      {product.product_code} - {product.product_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">
                  Color
                </label>
                <select
                  value={productColor}
                  onChange={(e) => setProductColor(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                >
                  <option value="">Select Color</option>
                  {colors.map((color) => (
                    <option key={color.id} value={color.product_color}>
                      {color.product_color}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Date Range Selection (optional, not used in API yet) */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Date Range</h3>
            <div className="flex gap-4">
              <div className="flex-1">
                <label htmlFor="start-date" className="block text-sm text-gray-600 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  id="start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="end-date" className="block text-sm text-gray-600 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  id="end-date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Export Button */}
          <div className="flex justify-end">
            <button
              onClick={handleExport}
              disabled={isExporting || isLoading}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {isExporting ? "Exporting..." : "Export Report"}
            </button>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SalesReport
